import { forgetAccount } from '../auth/gate';
import { api, WEB_VERSION, type Account, type Me } from '../sync/api';
import type { Saver } from '../sync/saver';
import { thDateTime } from '../time';
import { $ } from './dom';

/** "บัญชีของฉัน": who is signed in, until when, versions, the admin back office, and signing out. */
export function initAccountSheet(saver: Saver, opts: { openAdmin: () => void }) {
  let account: Account | null = null;
  let me: Me | null = null;
  let armedOut = false;
  let armTimer: ReturnType<typeof setTimeout> | undefined;
  const msg = $('acMsg'), outBtn = $('acOut') as HTMLButtonElement;
  const say = (text: string) => { msg.textContent = text; msg.hidden = !text; };

  function initial(a: Account) { return (a.name || a.email).trim().charAt(0).toUpperCase() || '?'; }

  function paint() {
    if (!account) return;
    $('acctBtnTxt').textContent = initial(account);
    $('acAvatar').textContent = initial(account);
    $('acName').textContent = account.name || account.email.split('@')[0];
    $('acEmail').textContent = account.email;
    $('acRole').textContent = account.role === 'admin' ? 'ผู้ดูแล' : 'สมาชิก';
    const lines: string[] = [];
    if (me) lines.push(`เข้าสู่ระบบเมื่อ ${thDateTime(new Date(me.session.createdAt))} · หมดอายุ ${thDateTime(new Date(me.session.expiresAt))}`);
    else lines.push('ออฟไลน์: ใช้งานต่อจากบัญชีที่เข้าสู่ระบบไว้ งานบันทึกในเครื่องนี้');
    $('acSession').textContent = lines.join('');
    const ver = $('acVersion');
    if (!me) { ver.textContent = `เวอร์ชันหน้าเว็บ ${WEB_VERSION}`; ver.classList.remove('err-text'); }
    else if (me.version === WEB_VERSION) { ver.textContent = `เวอร์ชัน ${WEB_VERSION} (หน้าเว็บและเซิร์ฟเวอร์ตรงกัน)`; ver.classList.remove('err-text'); }
    else { ver.textContent = `⚠ หน้าเว็บ ${WEB_VERSION} · เซิร์ฟเวอร์ ${me.version} ไม่ตรงกัน ลองเปิดหน้าใหม่`; ver.classList.add('err-text'); }
    const isAdmin = account.role === 'admin';
    $('acAdmin').hidden = !isAdmin;
    const badge = $('acPending');
    badge.hidden = !(isAdmin && me && me.pending > 0);
    badge.textContent = me ? `${me.pending} รออนุมัติ` : '';
    $('acctDot').hidden = badge.hidden;
  }

  function disarm() { armedOut = false; outBtn.textContent = 'ออกจากระบบ'; }

  outBtn.onclick = async () => {
    say('');
    if (!armedOut) {
      // try to get unsaved edits onto the server first; if that isn't possible, say why and ask again
      const why = await saver.flush();
      if (why) {
        armedOut = true; clearTimeout(armTimer); armTimer = setTimeout(disarm, 6000);
        say(`${why} งานนี้ยังเก็บอยู่ในเครื่องนี้ และจะขึ้นเซิร์ฟเวอร์เมื่อคุณเข้าสู่ระบบบัญชีนี้อีกครั้ง`);
        outBtn.textContent = 'แตะอีกครั้งเพื่อออกจากระบบ';
        return;
      }
    }
    outBtn.disabled = true;
    try {
      await saver.saveLocal();
      await api.logout();
      forgetAccount();
      location.replace('/');
    } catch (e) {
      say('ออกจากระบบไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)));
      outBtn.disabled = false; disarm();
    }
  };
  $('acAdmin').onclick = () => opts.openAdmin();

  return {
    set(a: Account, m: Me | null) { account = a; me = m; paint(); },
    /** Refresh when the panel opens (session times, pending count). */
    async refresh() {
      disarm(); say('');
      try { me = await api.me(); account = me.user; }
      catch (e) { if (me) say('อัปเดตข้อมูลบัญชีไม่ได้: ' + (e instanceof Error ? e.message : String(e))); }
      paint();
    },
    get account() { return account; }
  };
}
